import { Component, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ModalService } from '../../../../../../../services/modal.service';
import { NotificationService } from '../../../../../../../services/notifications.service';
import { FormControl } from '@angular/forms';
import { CHANNEL_TYPE, UPLOAD_FILE_TYPES } from '../../../../../../../enum/constants';
import { FileUploadNewComponent } from '../../../../../../../modals/file-upload-new/file-upload-new.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-group-images-picker',
  templateUrl: './group-images-picker.component.html',
  styleUrls: ['./group-images-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GroupImagesPickerComponent implements OnChanges {
  @Input() imagesControl!: FormControl<{ url: string }[] | null>;
  @Input() photosLimit: number = 0;

  protected emptyImageBlocks: void[] = [];

  constructor(
    private readonly translate: TranslateService,
    private readonly modalService: ModalService,
    private readonly notificationService: NotificationService,
  ) {}

  ngOnChanges(): void {
    this.setEmptyImageBlocks();
  }

  private setEmptyImageBlocks(): void {
    const current = this.imagesControl.value?.length || 0;
    const emptyCount = this.photosLimit - current;
    this.emptyImageBlocks = new Array(emptyCount > 0 ? emptyCount : 0);
  }

  protected onDrop(event: CdkDragDrop<string[]>): void {
    const images = this.imagesControl.value || [];
    moveItemInArray(images, event.previousIndex, event.currentIndex);
    this.imagesControl.setValue(images);
  }

  protected onDelete(index: number): void {
    const images = this.imagesControl.value || [];
    images.splice(index, 1);
    this.imagesControl.setValue(images);
    this.setEmptyImageBlocks();
  }

  protected onAddImage(): void {
    const images = this.imagesControl.value ?? [];

    if (images.length >= this.photosLimit) {
      this.notificationService.showWarning(
        this.translate.instant('notifications_texts.warn.warn') as string,
        this.translate.instant('notifications_texts.warn.no_more_upload_files', { limit: this.photosLimit }) as string,
      );
      return;
    }

    const allowedMediaTypes: string[] = UPLOAD_FILE_TYPES[CHANNEL_TYPE.OLX].allowed_media_types!;

    this.modalService
      .openModal({
        translate: true,
        component: FileUploadNewComponent,
        title: 'upload_file',
        height: 'auto',
        data: {
          length: images.length,
          max_upload_files: this.photosLimit,
          channel_type: CHANNEL_TYPE.OLX,
          allowed_media_types: allowedMediaTypes,
        },
      })
      .subscribe((res: { link: string }[]) => {
        if (!res) return;

        const updated = [...images, ...res.map((img: { link: string }) => ({ url: img.link }))];
        this.imagesControl.setValue(updated.slice(0, this.photosLimit));
        this.setEmptyImageBlocks();
      });
  }
}
